export function stripAnsi(value = '') {
  const ansiPattern = new RegExp(`${String.fromCharCode(27)}\\[[0-9;]*m`, 'g');
  return String(value).replace(ansiPattern, '');
}

export function classifyLogLine(line = '') {
  const plain = stripAnsi(line);
  const lower = plain.toLowerCase();
  const isSummaryLine = /^\d+ (feature|scenario|step)s? passed/.test(plain.trim());

  if (isSummaryLine) {
    return lower.includes('0 failed') ? 'success' : 'fail';
  }
  if (lower.includes('fail') || lower.includes('error')) return 'fail';
  if (lower.includes('pass') || lower.includes('success')) return 'success';
  return '';
}

export function statusLabel(status) {
  return status === 'fail' ? 'NOT FOUND' : 'FOUND';
}

export function describeAction(method = '') {
  const map = {
    click_element: 'Clicked on',
    find_element: 'Looked for',
    is_displayed: 'Checked visibility of',
    is_displayed_without_exception: 'Checked if visible:',
    scroll_to_element: 'Scrolled to find',
    scroll_to_element_and_click: 'Scrolled to and clicked',
    scroll_to_element_any_direction: 'Scrolled in both directions to find',
    send_keys: 'Typed text into',
    click_and_hold_element: 'Long-pressed on',
    click_with_optional_dialog: 'Clicked with dialog handling',
    swipe_down_from_element: 'Swiped down on',
    swipe_left_element: 'Swiped left on',
    reveal_element_action: 'Revealed actions on',
    is_text_present: 'Searched for text on screen:',
    toggle_switch: 'Toggled switch:',
    tap_on_element: 'Tapped on',
    swipe_left_to_access_element_and_click: 'Swiped to find and clicked',
  };
  return map[method] || String(method).replace(/_/g, ' ');
}

export function friendlyLocator(locatorValue) {
  if (!locatorValue) return 'unknown element';
  const value = String(locatorValue);
  const patterns = [
    /@text=['"](.*?)['"]/,
    /@content-desc=['"](.*?)['"]/,
    /@name=['"](.*?)['"]/,
    /@label=['"](.*?)['"]/,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return `"${match[1]}"`;
  }

  const resourceId = value.match(/resource-id=['"](.*?)['"]/);
  if (resourceId) {
    const raw = resourceId[1];
    const short = raw.includes('/') ? raw.split('/').pop() : raw;
    return short.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
  }

  if (!value.includes('/') && !value.includes('[') && value.length < 60) {
    return `"${value}"`;
  }

  return value.length > 80 ? `${value.substring(0, 77)}...` : value;
}

export function classifyEvents(events = []) {
  if (!Array.isArray(events)) return [];

  let lastFailIndex = -1;
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i].status === 'fail') {
      lastFailIndex = i;
      break;
    }
  }

  return events.map((event, index) => {
    if (event.status !== 'fail') return 'success';
    if (index < lastFailIndex) return 'skipped';

    const hasSuccessAfter = events
      .slice(index + 1)
      .some((nextEvent) => nextEvent.status === 'success');
    return hasSuccessAfter ? 'skipped' : 'fail';
  });
}

export function describeOutcome(event, isSkipped) {
  if (event?.status === 'success') {
    const durationMs = Number(event.duration_ms);
    const duration = Number.isNaN(durationMs) ? '' : ` (${(durationMs / 1000).toFixed(1)}s)`;
    return `OK${duration}`;
  }

  if (isSkipped) return 'Not present';

  const exceptionType = event?.exception_type || '';
  if (exceptionType === 'TimeoutException') return 'Timed out';
  if (exceptionType === 'NoSuchElementException') return 'Element not found';
  if (exceptionType === 'StaleElementReferenceException') {
    return 'Element disappeared';
  }
  if (exceptionType === 'ElementNotInteractableException') {
    return 'Not interactable';
  }
  if (exceptionType === 'InvalidSelectorException') return 'Bad locator';
  if (exceptionType.includes('Assertion')) return 'Assertion failed';
  return 'Failed';
}

export function explainException(exceptionType, method, locatorBy, locatorValue, durationMs) {
  const locator = `${locatorBy} = "${locatorValue}"`;
  const durationSeconds = Number(durationMs) / 1000;

  if (!exceptionType || exceptionType === '-') {
    if (!Number.isNaN(durationSeconds) && durationSeconds > 5) {
      return `The element (${locator}) was found, but it took ${durationSeconds.toFixed(1)}s. This can mean the app loaded slowly or the element appeared late.`;
    }
    return `The element (${locator}) was found successfully using "${method}".`;
  }

  const explanations = {
    TimeoutException: `The test waited for the element (${locator}) to appear, but it did not show up before the timeout.`,
    NoSuchElementException: `The element (${locator}) does not exist on the current screen, or the test reached a different screen than expected.`,
    StaleElementReferenceException: `The element (${locator}) was found, but the app refreshed or navigated before interaction finished.`,
    ElementNotInteractableException: `The element (${locator}) exists but cannot be interacted with. It may be hidden, disabled, or still loading.`,
    InvalidSelectorException: `The locator expression (${locator}) is invalid. This is likely a test-code selector issue.`,
    WebDriverException: `The Appium session or device connection failed during this step.`,
    AssertionError: `The assertion for (${locator}) failed because the actual screen state did not match the expected state.`,
  };

  return (
    explanations[exceptionType] ||
    `The "${method}" call failed with ${exceptionType} for element (${locator}).`
  );
}

export function summaryForEvent(event, isSkipped) {
  if (isSkipped) {
    return 'This element was not present, but the test continued normally.';
  }
  if (event?.status === 'fail') {
    return 'This screen element could not be found and the test stopped here.';
  }
  return 'This screen element was found successfully.';
}

export function explainDrift(item) {
  const drift = (item?.drift || item?.status || '').toLowerCase();
  const locator = `${item?.locator_by || item?.by || ''} = "${item?.locator_value || item?.value || ''}"`;
  const durationA = Number(item?.durationA ?? item?.duration_a);
  const durationB = Number(item?.durationB ?? item?.duration_b);

  if (drift === 'regression') {
    return `This element (${locator}) worked in the baseline version but fails in the new version.`;
  }
  if (drift === 'fixed') {
    return `This element (${locator}) failed before and works in the new version.`;
  }
  if (drift === 'added' || drift === 'new') {
    return `This element (${locator}) only appears in the new version.`;
  }
  if (drift === 'removed') {
    return `This element (${locator}) existed in the baseline version but is gone in the new version.`;
  }
  if (drift === 'failed_both') {
    return `This element (${locator}) fails in both versions, so it is a pre-existing issue.`;
  }

  if (!Number.isNaN(durationA) && !Number.isNaN(durationB)) {
    const delta = durationB - durationA;
    if (delta > 50) {
      return `This element works in both versions but got slower (${durationA.toFixed(0)}ms to ${durationB.toFixed(0)}ms).`;
    }
    if (delta < -50) {
      return `This element works in both versions and got faster (${durationA.toFixed(0)}ms to ${durationB.toFixed(0)}ms).`;
    }
  }

  return `This element behaves the same way in both versions.`;
}

export function summaryForDrift(item) {
  const drift = (item?.drift || item?.status || '').toLowerCase();
  const summaries = {
    regression: 'Working before, broken now.',
    fixed: 'Broken before, fixed now.',
    stable: 'Same behavior in both versions.',
    passed: 'Same behavior in both versions.',
    added: 'New in the updated version.',
    new: 'New in the updated version.',
    removed: 'Removed from the updated version.',
    failed_both: 'Failing in both versions.',
  };
  return summaries[drift] || 'Status changed.';
}

export function explainBeaconImpact(item) {
  const versionA = item?.version_a || item?.a;
  const versionB = item?.version_b || item?.b;
  const name =
    item?.event_name || versionA?.event_name || versionB?.event_name || `Beacon ${item?.beacon_id}`;
  const drift = (item?.status || item?.drift || '').toUpperCase();
  const missingInB = versionB ? !versionB.found : item?.found_b === false;

  const impacts = {
    PASSED: `${name} was triggered in both versions.`,
    REGRESSION: `${name} fired in the baseline version but is now ${missingInB ? 'missing' : 'not valid'}.`,
    FIXED: `${name} was not valid before and is now firing.`,
    FAILED_BOTH: `${name} fails in both versions.`,
    NEW: `${name} is new in the updated version.`,
    REMOVED: `${name} was present before and is gone now.`,
  };

  return impacts[drift] || `Status changed for ${name}.`;
}

export function explainBeaconDetail(item, priority = 'P2') {
  const versionA = item?.version_a || item?.a;
  const versionB = item?.version_b || item?.b;
  const name =
    item?.event_name || versionA?.event_name || versionB?.event_name || `Beacon ${item?.beacon_id}`;
  const drift = (item?.status || item?.drift || '').toUpperCase();
  const priorityNote =
    priority === 'P0'
      ? 'This is a critical P0 business event.'
      : priority === 'P1'
        ? 'This is an important P1 event.'
        : '';
  const missingInB = versionB ? !versionB.found : item?.found_b === false;

  const details = {
    PASSED: `The "${name}" beacon was triggered in both versions. ${priorityNote}`,
    REGRESSION: `The "${name}" beacon fired in the baseline version but is now ${missingInB ? 'missing' : 'not valid'}. ${priorityNote}`,
    FIXED: `The "${name}" beacon was not valid before but is now firing. ${priorityNote}`,
    FAILED_BOTH: `The "${name}" beacon fails in both versions. ${priorityNote}`,
    NEW: `The "${name}" beacon appears only in the new version. ${priorityNote}`,
    REMOVED: `The "${name}" beacon existed in the baseline version but no longer appears. ${priorityNote}`,
  };

  return details[drift] || `Status changed for ${name}. ${priorityNote}`;
}

export function buildPriorityMap(rawPriorities) {
  const raw = rawPriorities?.priorities || rawPriorities || {};
  const map = {};

  Object.entries(raw).forEach(([level, ids]) => {
    if (Array.isArray(ids)) {
      ids.forEach((id) => {
        map[String(id)] = level;
      });
    }
  });

  return map;
}

export function getPriority(priorityMap, beaconId) {
  return priorityMap?.[String(beaconId)] || 'P2';
}

export function isLocatorIgnored(ignoreList, by, value) {
  return (ignoreList?.locators || []).some(
    (locator) => locator.by === by && locator.value === value,
  );
}

export function isBeaconIgnored(ignoreList, beaconId) {
  return (ignoreList?.beacons || []).some((beacon) => beacon.id === String(beaconId));
}

export function formatDurationMs(value) {
  const numeric = Number(value);
  return Number.isNaN(numeric) ? '-' : `${numeric.toFixed(1)} ms`;
}

export function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}
